<script lang="ts" setup>
import {
  ToccoBaseModuleFieldsFragment,
  ToccoBaseModuleQueryDocument,
  ToccoBaseModuleQueryQuery,
  ToccoBaseModuleQueryQueryVariables,
  ToccoUser,
} from '~/api/graphql/generated'
import { useTypedApolloClients } from '~/lib/utils/graphqlDocument'
import { normalizeDurationDays, processToccoUser } from '~/lib/utils/toccoUtils'

const props = defineProps<{
  controller: ReturnType<typeof useModal>
  moduleId: string | null
}>()

const { t } = useI18n()
const localePath = useLocalePath()
const clients = useTypedApolloClients()

const target = props.controller.target
const modalRef = props.controller.modalRef
const closeButtonRef = props.controller.closeButtonRef

const isOpen = computed(() => props.controller.isOpen.value)
const module = ref<ToccoBaseModuleFieldsFragment | null>(null)
let requestSequence = 0

watch(
  () => [isOpen.value, props.moduleId] as const,
  async ([open, id]) => {
    const requestId = ++requestSequence
    module.value = null
    if (!open || !id) return
    if (!clients?.default) return
    try {
      const result = await clients.default.query<ToccoBaseModuleQueryQuery, ToccoBaseModuleQueryQueryVariables>({
        query: ToccoBaseModuleQueryDocument,
        variables: { id },
      })
      if (requestId !== requestSequence || !isOpen.value || props.moduleId !== id) return
      module.value = (result.data.toccoQuery?.entities?.[0] as ToccoBaseModuleFieldsFragment | undefined) ?? null
    } catch {
      if (requestId !== requestSequence) return
      module.value = null
    }
  },
)

const subtitle = computed(() => {
  if (!module.value) return ''
  const eventType = module.value.fieldEventType?.entity?.name ?? ''
  const durationDays = normalizeDurationDays(module.value.fieldDurationDays)
  const duration = durationDays ? t('tocco.durationDays', durationDays) : ''
  return [eventType, duration].filter(Boolean).join(': ')
})

const description = computed(() =>
  module.value?.fieldBaseModuleDescription?.value
    ? module.value.fieldBaseModuleDescription.value.split('\n').join('<br/>')
    : '',
)

const moduleLink = computed(() => {
  const path = module.value?.entityUrl?.path
  return path ? localePath({ path, hash: '#dates' }) : ''
})

const lecturers = computed(() => {
  const lecturersList = module.value?.fieldLecturers || []
  return lecturersList
    .map((userData) => {
      const user = userData?.entity as ToccoUser
      return {
        lastName: user?.fieldLastName?.toLowerCase() || '',
        processed: processToccoUser(user, true),
      }
    })
    .sort((a, b) => a.lastName.localeCompare(b.lastName))
    .map((item) => item.processed)
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isOpen"
      ref="modalRef"
      class="overlay"
      role="dialog"
      aria-modal="true"
      :aria-label="module?.title ?? ''"
      :aria-busy="!module"
      @keydown="controller.handleKeydown"
    >
      <div ref="target" class="modal">
        <button ref="closeButtonRef" class="close" type="button" :aria-label="t('close')" @click="controller.close">
          <Icon name="large-close" />
        </button>

        <div v-if="module" class="content">
          <h1 class="title">{{ module.title }}</h1>
          <p v-if="subtitle" class="subtitle text-medium">{{ subtitle }}</p>
          <div v-if="description" class="description" v-html="description" />
          <Button
            v-if="moduleLink"
            primary
            slide
            :to="moduleLink"
            class="single-booking-button"
            @click="controller.close"
          >
            {{ t('tocco.moduleOverlay.singleBooking') }}
          </Button>

          <ToccoAccordion :entity="module" />

          <template v-if="lecturers.length">
            <h2 class="lecturers-title text-xlarge">{{ t('tocco.detail.lecturersTitle') }}</h2>
            <ul class="lecturers cul">
              <li v-for="lecturer in lecturers" :key="lecturer.name" class="lecturer">
                <span class="name text-medium">{{ lecturer.name }}</span>
                <span v-if="lecturer.position" class="position text-small">{{ lecturer.position }}</span>
              </li>
            </ul>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  background-color: var(--c-black-20);
  z-index: 102;
}

.modal {
  position: absolute;
  // CARW-1704 overrides the former 40-80px fluid top gap: the sheet reaches
  // the viewport top (Firefox repro 2026-09-07, sheet y=80 flagged as defect).
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  padding: 40px 20px;

  background-color: var(--c-white);
  overflow-y: scroll;

  @include breakpoint(ts) {
    padding: 120px 95px;
  }
}

.close {
  position: absolute;
  top: 10px;
  right: 10px;

  display: grid;
  place-items: center;

  width: 44px;
  height: 44px;
  padding: 0;

  color: var(--c-primary);
  background: var(--c-blue-light-40);
  border: 0;
  border-radius: 50%;

  @include transitions(background-color);

  @include hover {
    background: var(--c-blue-light-20);
  }

  @include breakpoint(ts) {
    top: 40px;
    right: 40px;
    width: 60px;
    height: 60px;
  }

  svg {
    width: 25px;
    height: 25px;

    @include breakpoint(ts) {
      width: 30px;
      height: 30px;
    }
  }
}

.content {
  max-width: 948px;
  width: 100%;
  margin-inline: auto;
}

.title {
  margin-bottom: 0;
}

.subtitle {
  color: var(--c-primary);
  font-weight: bold;
}

.description {
  color: var(--c-primary);
  @include fluid(margin-block, 30px, 40px);
}

.single-booking-button {
  @include fluid(margin-bottom, 30px, 40px);
}

.lecturers-title {
  @include fluid(margin-top, 40px, 60px);
}

.lecturers {
  display: grid;
  grid-template-columns: 100%;
  gap: var(--gap);

  @include breakpoint(ts) {
    grid-template-columns: 1fr 1fr;
  }
}

.lecturer {
  display: flex;
  flex-direction: column;
}

.name {
  color: var(--c-primary);
  font-weight: bold;
}
</style>
